import React from 'react';
import styles from './hero-pill.module.scss';

export interface HeroPillProps {
    text: string;
    imageSrc: {source: string; className: string}[];
    background?: string; // CSS color or gradient
    className?: string;
    /**
     * Hover effect style: 'apart' (default, moves text/image away horizontally)
     * or 'float' (text/image float up and overlap pill)
     */
    hoverEffect?: 'apart' | 'float';
    textColor?: 'white' | 'black';
}

/**
 * HeroPill - A pill-shaped component with customizable background, text color, left-aligned text, and right-aligned image.
 *
 * @param {string} text - The text to display inside the pill (left-aligned).
 * @param {string|string[]} imageSrc - The image URL(s) to display inside the pill (right-aligned). Can be a single URL or an array of up to 3 URLs.
 * @param {string|string[]} [imageAlt] - The alt text(s) for the image(s).
 * @param {string} [background] - The background color or gradient for the pill.
 * @param {string} [className] - Additional class names for the pill.
 * @param {string} [hoverEffect] - The hover effect style for the pill.
 * @param {string} [textColor] - The text color for the pill.
 */
export function HeroPill({
    text,
    imageSrc,
    background = '#f3f4f6',
    className = '',
    hoverEffect = 'apart',
    textColor = 'black',
}: HeroPillProps) {
    // Convert single image to array for consistent handling
    const imageSources = Array.isArray(imageSrc) ? imageSrc : [imageSrc];

    // Limit to maximum 3 images
    const imagesToRender = imageSources.slice(0, 3);

    return (
        <div
            className={`${styles.heroPill} ${styles[hoverEffect]} ${styles[className]}`}
            style={{background}}
            tabIndex={0}
            aria-label={text}
        >
            <span className={styles.text} style={{color: textColor === 'white' ? '#fff' : '#222'}}>
                {text}
            </span>
            {imagesToRender.map(({source, className}, index) => (
                <div
                    key={`${source}-${index}`}
                    className={`${styles.image} ${styles[className]}`}
                    style={{
                        backgroundImage: `url(${source})`,
                    }}
                />
            ))}
        </div>
    );
}
